import { useMemo, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../../utils/Auth';
import { DndContext, closestCenter, useDndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableField from './SortableField';
import { saveTrackerSurveyData, normalizeFieldTypes } from '../../../../utils/QRFunctions/qrActions';
import { getQrDataFieldType } from '../../../../lib/getQrDataFieldType';
import { getCustomFieldTypes } from '../../../../lib/customFieldTypes';
import FieldMenu from './FieldMenu';
import { motion, AnimatePresence } from 'motion/react';
import { transition, variants } from './variants1';
import { useWindowSize } from '../../../../utils/hooks/useWindowSize';
import { useClickOutside } from '../../../../utils/hooks/useClickOutside';
import styles from './Fields.module.css';

export default function Fields(props) {
    const { fields, reference, originalFields } = props;
    const { qr_data, dataDispatch, config } = useAuth();
    const { width } = useWindowSize();
    const qrTypeParent = useMemo(() => getQrDataFieldType(qr_data.data.qr_type), [qr_data?.data?.qr_type]);
    const fieldTypes = useMemo(() => {
        if (!config) return {};
        const merged = {};

        const legacySources = [config.columns, config.questions].filter(Boolean);
        for (const source of legacySources) {
            for (const [key, value] of Object.entries(source)) {
                if (!merged[key]) {
                    merged[key] = value;
                }
            }
        }

        if (config.field_types?.fields) {
            for (const [key, value] of Object.entries(config.field_types.fields)) {
                merged[key] = value;
            }
        }
        Object.assign(merged, getCustomFieldTypes(qr_data?.data?.qr_type));
        return merged;
    }, [config?.field_types?.fields, config?.columns, config?.questions, qr_data?.data?.qr_type]);

    const headerType = useMemo(() => qrTypeParent === 'tracker' ? 'Fields' : 'Questions' ,[qrTypeParent]);
    const normalizedRef = useRef(false);

    // One-time normalization effect for legacy keys (questions/columns -> fields)
    useEffect(() => {
        if (normalizedRef.current) return;
        if (reference === 'new') return;
        const trackerHasLegacy = qr_data?.data?.tracker?.columns && !qr_data?.data?.tracker?.fields;
        const surveyHasLegacy = qr_data?.data?.survey?.questions && !qr_data?.data?.survey?.fields;
        if (!trackerHasLegacy && !surveyHasLegacy) return;

        normalizeFieldTypes({ qrId: qr_data.id, qrData: qr_data }).then(res => {
            if (res.normalized) {
                normalizedRef.current = true;
                // Update local state with migrated fields + flag
                if (res.trackerCount >= 0 && qr_data?.data?.tracker) {
                    dataDispatch({ type: 'tracker', data: { fields: qr_data.data.tracker.fields || qr_data.data.tracker.columns || [], fields_normalized: true } });
                }
                if (res.surveyCount >= 0 && qr_data?.data?.survey) {
                    dataDispatch({ type: 'survey', data: { fields: qr_data.data.survey.fields || qr_data.data.survey.questions || [], fields_normalized: true } });
                }
            }
        });
    }, [reference, qr_data.id, qr_data?.data?.tracker?.columns, qr_data?.data?.survey?.questions, qr_data?.data?.tracker?.fields, qr_data?.data?.survey?.fields, dataDispatch]);

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const onDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const activeIndex = fields.indexOf(fields.find(col => col.id === active.id));
            const overIndex = fields.indexOf(fields.find(col => col.id === over.id));
            const reorderedFieldsArray = reorder(fields, activeIndex, overIndex);
            const data = {
                ...qr_data?.data?.[qrTypeParent],
                fields: reorderedFieldsArray
            };
            const id = qr_data?.id;
            dataDispatch({ type: qrTypeParent, data: { fields: reorderedFieldsArray } });
            if (reference !== 'new') {
                saveTrackerSurveyData({ id, data, qr_data, qrTypeParent })();
            };
        };
    };

    const [ scroll, setScroll ] = useState(false);
    const handleScroll = (e) => {
        const scrollTop = e.currentTarget.scrollTop;

        if (scrollTop > 0) {
            setScroll(true);
        } else {
            setScroll(false);
        }
    };

    const menuBtnRef = useRef(null);
    const [ menuRef, showMenu, setShowMenu ] = useClickOutside(false, menuBtnRef);
    const bottomRef = useRef(null);
    const rightRef = useRef(null);
    const contentRef = useRef(null);
    const droppableRef = useRef(null);
    const previousFieldsLength = useRef(fields?.length || 0);
    const newlyAddedFieldId = useRef(null);

    useEffect(() => {
        const currentLength = fields?.length || 0;

        if (currentLength <= previousFieldsLength.current) {
            previousFieldsLength.current = currentLength;
            return;
        }

        // Identify the newly added field (last non-archived field)
        const newField = fields?.filter(f => !f.archived)[fields?.filter(f => !f.archived).length - 1];
        if (newField) {
            newlyAddedFieldId.current = newField.id;
        }

        previousFieldsLength.current = currentLength;

        if (!fields || fields.length === 0) return;
        const bottomEl = bottomRef.current;
        const rightEl = rightRef.current;
        if (!bottomEl || !rightEl) return;

        const isFullyInView = (el) => {
            if (!el) return true;
            const rect = el.getBoundingClientRect();
            return rect.top >= 0 && rect.bottom <= window.innerHeight;
        };

        const to = setTimeout(() => {
            if (!isFullyInView(bottomEl)) {
                const rect = bottomEl.getBoundingClientRect();
                const delta = rect.bottom - window.innerHeight;
                window.scrollBy({ top: Math.max(delta, 0), left: 0, behavior: 'smooth' });
            }

            rightEl.scrollTo({ top: rightEl.scrollHeight, left: 0, behavior: 'smooth' });

            // Auto-click the newly added field to open it for editing (only on desktop)
            if (newlyAddedFieldId.current && width > 768) {
                const fieldElement = document.querySelector(`[data-field-id="${newlyAddedFieldId.current}"]`);
                if (fieldElement) {
                    fieldElement.click();
                    newlyAddedFieldId.current = null;
                }
            }
        }, 100);

        return () => {
          clearTimeout(to);
        };
    }, [fields.length]);

    return (
        <>
        <div className={styles.header}>Form Editor</div>
        <div className={styles.body}>
            <div className={styles.top}>
                <div className={styles.left} style={{flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
                    <h5 className={`${styles.title} ${scroll ? styles.titleScrolled : null}`}>Field Select</h5>
                </div>
                <div className={styles.right} style={{flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
                    <AnimatePresence initial={false}>
                        {width <= 768 && (
                            <motion.button
                                className={styles.menuBtn}
                                transition={{duration: .2}}
                                initial={{opacity: 0}}
                                animate={{opacity: 1}}
                                exit={{opacity: 0}}
                                onClick={() => setShowMenu(!showMenu)}
                                ref={menuBtnRef}
                            >
                                <i className='far fa-plus' />
                                {width > 600 && ('Add Field')}
                            </motion.button>
                        )}
                    </AnimatePresence>
                    <h5 className={styles.title}>Active {headerType}</h5>
                </div>
            </div>
            <div className={styles.bottom} ref={bottomRef}>
                <motion.div 
                    className={styles.left} 
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={width <= 768 ? showMenu ? 'show' : 'hide' : 'show'}
                    ref={menuRef}
                >
                    <FieldMenu 
                        qrTypeParent={qrTypeParent}
                        fields={fields}
                        handleScroll={handleScroll}
                        setShowMenu={setShowMenu}
                    />
                </motion.div>
                <div className={styles.right} ref={rightRef}>
                    <div className={styles.content} ref={contentRef}>
                        <div className={styles.form}>
                            {fields?.filter(field => !field?.archived)?.length > 0 ? (
                                <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                                    <DraggableFields
                                        fields={fields?.filter(field => !field?.archived)}
                                        config={fieldTypes}
                                        qrTypeParent={qrTypeParent}
                                        containerRef={droppableRef}
                                        reference={reference}
                                        originalFields={originalFields}
                                    />
                                </DndContext>
                            ) : (
                                <div className={styles.noFields}>
                                    <span><i className='fas fa-arrow-left fa-beat' /></span>
                                    Select from the menu on the left to get started
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        {(fields?.filter(field => field?.archived)?.length > 0) &&
            <div className={styles.body}>
                <div className={styles.title}>Archived {headerType}</div>
                <div className={styles.content}>
                    <div className={styles.form}>
                        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                            <DraggableFields
                                fields={fields?.filter(field => field?.archived)}
                                config={fieldTypes}
                                qrTypeParent={qrTypeParent}
                                reference={reference}
                                originalFields={originalFields}
                            />
                        </DndContext>
                    </div>
                </div>
            </div>
        }
        </>
    );
}

const DraggableFields = ({ fields, config, qrTypeParent, containerRef, reference, originalFields}) => {
    const { active } = useDndContext();
    const isAnyFieldDragging = !!active;
    return (
        <div className={styles.droppableDiv} ref={containerRef}>
            <SortableContext items={fields.map(field => field.id)} strategy={verticalListSortingStrategy} >
                {fields?.map((field) => (
                    <SortableField 
                        key={field?.id}
                        fields={fields}
                        field={field} 
                        config={config[field.type]} 
                        isAnyFieldDragging={isAnyFieldDragging}
                        qrTypeParent={qrTypeParent}
                        reference={reference}
                        originalFields={originalFields}
                    />
                ))}
            </SortableContext>
        </div>
    );
};
